import React, { useCallback, useState, useEffect } from 'react';
import { Model, StylesManager } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';
import styled from 'styled-components';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import { green } from '@material-ui/core/colors';
// Import Material-UI components for the custom completion screen
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import CheckBoxRoundedIcon from '@material-ui/icons/CheckBoxRounded';
// ----------------------------------------------------------------------
// Assuming these utility and API imports are correctly resolved in your environment
import { getAnsweredQuestionsCount, getQuestionsCount } from '../utils';
import api, { uploadFile } from '../../../Utils/api';
import { formLibraryStyle } from './FormLibraryStyle';

StylesManager.applyTheme('flat');

const GreenCheckbox = withStyles({
  root: {
    color: green[400],
    '&$checked': { color: green[600] },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

// Define a consistent height for the progress bar wrapper for spacing
const PROGRESS_BAR_HEIGHT_WITH_PADDING = '50px';

const ProgressBarWrapper = styled.div`
  position: fixed;
  max-width: 700px;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  background-color: white;
  box-shadow: 0 0 8px rgba(0, 12, 63, 0.15);
  padding: 1em; 
  z-index: 10;
  display: flex;
  align-items: center; 
  height: ${PROGRESS_BAR_HEIGHT_WITH_PADDING}; 
  box-sizing: border-box; 

  @media only screen and (max-width: 1200px) {
    max-width: initial;
  }
`;

const ProgressBarInner = styled.div`
  position: relative;
  height: 1em;
  width: 100%;
  border-radius: 2em;
  overflow: hidden;
`;

const ProgressBarBase = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 12, 63, 0.1);
`;

const ProgressBar = styled.div`
  position: absolute;
  left: 0;
  width: ${(props) => Math.ceil(props.width)}%;
  height: 100%;
  background: #1bd588;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.div`
  font-size: 0.875em;
  font-weight: 500;
  opacity: 0.5;
  white-space: nowrap;
  margin-left: 1em;
`;

const ClipWrapper = styled.div`
  text-align: center;
  margin-top: 100px;
`;

// New styled component to push the survey down
const SurveyContainer = styled.div`
  width: 100%;
  margin-top: ${PROGRESS_BAR_HEIGHT_WITH_PADDING}; 
`;

// *** NEW STYLED COMPONENT FOR VERTICAL CENTERING ***
const CenteredCompletionWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center; 
  align-items: center; 
  min-height: 100vh; 
  padding-top: 20px; 
`;
// **************************************************

const TargetLanguageCode = 'en';

const useStyles = makeStyles((theme) => ({
  checkIcon: {
    color: green[500],
    marginBottom: theme.spacing(2) || '16px',
  },
  thankyou: {
    fontSize: '1.5rem',
    marginBottom: theme.spacing(1) || '8px',
  },
  instructions: {
    textAlign: 'center',
    color: '#666',
    marginBottom: theme.spacing(3) || '24px',
  },
  btnContinue: {
    marginTop: theme.spacing(2) || '16px',
  },
  completionWrapper: {
    padding: theme.spacing(5) || '40px',
    maxWidth: 600,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  }
}));

// Custom completion screen component
const CustomCompletionScreen = React.memo(({ handleEditAnswer, TargetLanguageCode, classes }) => {
  // ... (unchanged) ...
  const thankYouText = {
    es: 'Gracias',
    vi: 'Cảm ơn',
    cmn: '谢谢',
    en: 'Thank You'
  };
  const instructionText = {
    es: 'El cuestionario se presentó con éxito. El abogado tal vez le llame para pedirle más aclaraciones.',
    vi: 'Bảng câu hỏi đã được gửi thành công. Luật sư có thể gọi cho bạn để làm rõ thêm.',
    cmn: '问卷已成功提交。律师可能会打电话给您以进一步澄清。',
    en: 'The questionnaire was submitted successfully. The Lawyer may call you for further clarification.'
  };
  const editLinkText = {
    es: 'Haga clic aquí si necesita modificar su respuesta',
    vi: 'Nhấp vào đây nếu bạn cần sửa đổi câu trả lời của mình',
    cmn: '如果您需要修改您的回复，请点击此处',
    en: 'Click here if you need to modify your response'
  };

  const lang = TargetLanguageCode || 'en';

  return (
    <Grid container justifyContent="center" spacing={2} className={classes.completionWrapper}>
      <Grid item xs={12}>
        <CheckBoxRoundedIcon className={classes.checkIcon} style={{ width: '100%', maxWidth: '100px', height: '100px' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" className={classes.thankyou} component="h2">
          <b>{thankYouText[lang] || thankYouText.en}</b>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography className={classes.instructions}>
          {instructionText[lang] || instructionText.en}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Link className={classes.btnContinue}
          component="button"
          variant="body2"
          color="primary"
          underline="always"
          onClick={handleEditAnswer}
        >
          {editLinkText[lang] || editLinkText.en}
        </Link>
      </Grid>
    </Grid>
  );
});


// =========================================================================
// !!! CUSTOM PAGINATION LOGIC (FIXED HOOKS) !!!
// =========================================================================

// Utility function to calculate the page numbers array
const getPageNumbers = (currentPageNo, totalPages, range = 5) => {
  const pages = [];
  const currentPage = currentPageNo + 1;

  if (totalPages <= range + 2) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  pages.push(1);

  const start = Math.max(2, currentPage - Math.floor(range / 2));
  const end = Math.min(totalPages - 1, currentPage + Math.ceil(range / 2) - 1);

  let windowStart = start;
  let windowEnd = end;

  if (windowStart <= 2) {
    windowStart = 2;
    windowEnd = Math.min(totalPages - 1, range + 1);
  }

  if (windowEnd >= totalPages - 1) {
    windowEnd = totalPages - 1;
    windowStart = Math.max(2, totalPages - range);
  }

  if (windowStart > 2) {
    pages.push('...');
  }

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  if (windowEnd < totalPages - 1) {
    pages.push('...');
  }

  if (totalPages > 1) {
    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }
  }

  return pages.filter((value, index, self) =>
    self.indexOf(value) === index
  );
};


// --- Styled Component for the Navigator (Mobile Friendly) ---
const NavigatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  margin-top: 10px;
  flex-wrap: wrap; 
`;

const NavButton = styled.button`
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  margin: 2px 3px;
  padding: 8px 12px; 
  cursor: pointer;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: ${(props) => (props.active ? '#1bd588' : 'white')};
  color: ${(props) => (props.active ? 'white' : 'black')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    background-color: ${(props) => (props.active ? '#15a065' : '#f0f0f0')};
  }
`;

const Ellipsis = styled.span`
  margin: 0 5px;
  padding: 5px 0;
  color: #666;
  font-weight: bold;
`;

// --- Custom Navigator React Component (FIXED) ---
const CustomPageNavigator = React.memo(({ survey }) => {
  // 🐛 FIX: Call Hooks UNCONDITIONALLY at the top level
  // Initialize state with a default value (0) or the current page if survey exists
  const [currentPageNo, setCurrentPageNo] = useState(survey ? survey.currentPageNo : 0);

  // 🐛 FIX: Call useEffect UNCONDITIONALLY
  useEffect(() => {
    // Only subscribe and update if the survey model exists
    if (!survey) return;

    const pageChangeHandler = () => setCurrentPageNo(survey.currentPageNo);

    // Ensure state reflects the actual survey page on initial mount/survey change
    setCurrentPageNo(survey.currentPageNo);

    survey.onCurrentPageChanged.add(pageChangeHandler);

    return () => {
      // Clean up event listener
      if (survey) survey.onCurrentPageChanged.remove(pageChangeHandler);
    };
  }, [survey]);

  // 4. Conditional return AFTER all hooks are declared
  if (!survey || survey.visiblePageCount <= 1) return null;

  const totalPages = survey.visiblePageCount;
  const pageNumbers = getPageNumbers(currentPageNo, totalPages);

  // Need current page number for button highlighting and disable logic
  const currentPage = currentPageNo + 1;

  const setPage = (pageNumber) => {
    // SurveyJS uses 0-based index
    if (survey) {
      survey.currentPageNo = pageNumber - 1;
    }
  };

  return (
    <NavigatorWrapper>
      <NavButton onClick={() => survey.prevPage()} disabled={currentPageNo === 0}>
        &laquo; Previous
      </NavButton>

      {pageNumbers.map((page, index) => (
        page === '...' ? (
          <Ellipsis key={`dot-${index}`}>...</Ellipsis>
        ) : (
          <NavButton
            key={page}
            onClick={() => setPage(page)}
            active={parseInt(page) === currentPage}
            aria-current={parseInt(page) === currentPage ? 'page' : undefined}
          >
            {page}
          </NavButton>
        )
      ))}

      <NavButton onClick={() => survey.nextPage()} disabled={currentPageNo === totalPages - 1}>
        Next &raquo;
      </NavButton>
    </NavigatorWrapper>
  );
});

// =========================================================================
// !!! END CUSTOM PAGINATION LOGIC !!!
// =========================================================================


function App(props) {
  const { masstortQuestionsRecords, clientData, answerData = {}, existingQuestions = false, selectedQuestionsData } = props;
  console.log('props', props)
  const classes = useStyles();

  const [survey, setSurvey] = useState(null);
  const [editedQuestionsCount, setEditedQuestionsCount] = useState(0);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);

  const selectedQuestionsRef = React.useRef([]);
  const editedQuestionsRef = React.useRef({});

  const handleEditAnswer = useCallback(() => {
    window.location.reload();
  }, []);


  useEffect(() => {
    if (masstortQuestionsRecords?.length > 0) {
      // Group questions into pages of 10 each
      const chunkSize = 5;
      const pages = [];

      for (let i = 0; i < masstortQuestionsRecords.length; i += chunkSize) {
        const chunk = masstortQuestionsRecords.slice(i, i + chunkSize);
        pages.push({
          name: `page${i / chunkSize + 1}`,
          elements: chunk,
        });
      }

      // Create SurveyJS model with paginated questions
      const surveyModel = new Model({
        logoPosition: 'right',
        pages,
      });

      surveyModel.applyTheme(formLibraryStyle);
      surveyModel.questionsOnPageMode = 'standard'; // multiple questions per page
      surveyModel.data = answerData || {};
      surveyModel.completedHtml = ""; // disable default completion page
      surveyModel.sendResultOnPageNext = true;

      surveyModel.onPartialSend.add(async (sender) => {
        const partialData = sender.data;
        console.log('partialData', partialData)
        try {
          // --- replicate your save logic ---
          const traverseAndMerge = (elements, answer) =>
            elements.map((item) => {
              const updatedItem = { ...item, responseValue: answer?.[item.name] ?? null };
              if (item.type === "panel")
                updatedItem.elements = traverseAndMerge(item.elements, answer);
              if (item.type === "paneldynamic" && item.templateElements)
                updatedItem.templateElements = traverseAndMerge(item.templateElements, answer?.[item.name]);
              return updatedItem;
            });

          const mergedQuestion = traverseAndMerge(masstortQuestionsRecords, partialData);
          const finalResponses = existingQuestions
            ? mergeQuestions(existingQuestions, mergedQuestion)
            : mergedQuestion;

          const answeredCount = getAnsweredQuestionsCount(finalResponses);
          const totalCount = getQuestionsCount(finalResponses);

          const resultJson = { result: finalResponses, answers: partialData };

          const res = await api.post(`/rest/mass-tort-client-forms`, {
            case_id: clientData?.case_id,
            client_id: clientData?.client_id,
            answered_by: "client",
            form_otp_id: clientData.id,
            answered_count: answeredCount,
            selected_questions_data: selectedQuestionsData,
          });

          if (res?.data) {
            await uploadFile(res.data.questions_upload_url, resultJson, "application/json");
            await api.put("/rest/mass-tort-client-forms/save-s3key-url", {
              case_id: clientData?.case_id,
              client_id: clientData?.client_id,
              questions_upload_url: res.data.questions_upload_url,
              questions_public_url: res.data.questions_public_url,
              questions_s3_file_key: res.data.questions_s3_file_key,
              questions_count: totalCount,
              answered_count: answeredCount,
              selected_questions_data: selectedQuestionsData,
            });
          }

          console.log("Auto-save successful");
        } catch (error) {
          console.error("Auto-save failed:", error);
        }
      })

      // Show navigation and progress bar
      // surveyModel.showNavigationButtons = true;
      // surveyModel.showProgressBar = 'bottom'; // you can change to 'top' or 'both'

      // Handle value changes for progress bar
      surveyModel.onValueChanged.add((_, { name, value }) => {
        editedQuestionsRef.current[name] = value;
        const count = Object.values(editedQuestionsRef.current).filter(v =>
          v !== '' && v !== null && v !== undefined &&
          !(Array.isArray(v) && v.length === 0) &&
          !(typeof v === 'object' && Object.keys(v).length === 0)
        ).length;
        setEditedQuestionsCount(count);
      });

      setSurvey(surveyModel);
    }
  }, [masstortQuestionsRecords, answerData]);


  // Merge utility (unchanged)
  const mergeQuestions = (existing, current) => {
    const currentPanelMap = new Map();
    const currentQuestionsMap = new Map();

    current?.forEach((item) => {
      if (item.type === 'panel') currentPanelMap.set(item.panelId, item);
      else if (item.questionId) currentQuestionsMap.set(item.questionId, item);
    });

    const mergedQuestions = existing.map((existingItem) => {
      if (existingItem.type === 'panel' && currentPanelMap.has(existingItem.panelId)) {
        const currentPanel = currentPanelMap.get(existingItem.panelId);
        const mergedElements = [...existingItem.elements];
        currentPanel.elements.forEach((currentElement) => {
          const idx = mergedElements.findIndex(e => e.questionId === currentElement.questionId);
          if (idx === -1) mergedElements.push(currentElement);
          else mergedElements[idx] = { ...mergedElements[idx] };
        });
        return { ...existingItem, elements: mergedElements };
      }
      if (existingItem.questionId && currentQuestionsMap.has(existingItem.questionId)) {
        return currentQuestionsMap.get(existingItem.questionId);
      }
      return existingItem;
    });

    current.forEach((currentItem) => {
      if (currentItem.type === 'panel' && !mergedQuestions.some(i => i.panelId === currentItem.panelId)) mergedQuestions.push(currentItem);
      if (!mergedQuestions.some(i => i.questionId === currentItem.questionId)) mergedQuestions.push(currentItem);
    });

    return mergedQuestions;
  };

  // On survey complete
  const surveyComplete = useCallback(async (surveyModel) => {
    const responseData = surveyModel.data;

    const traverseAndMerge = (elements, answer) => elements.map((item) => {
      const updatedItem = { ...item, responseValue: answer?.[item.name] ?? null };
      if (item.type === 'panel') updatedItem.elements = traverseAndMerge(item.elements, answer);
      if (item.type === 'paneldynamic' && item.templateElements) updatedItem.templateElements = traverseAndMerge(item.templateElements, answer?.[item.name]);
      return updatedItem;
    });

    const mergedQuestion = traverseAndMerge(masstortQuestionsRecords, responseData);
    const finalResponses = existingQuestions ? mergeQuestions(existingQuestions, mergedQuestion) : mergedQuestion;

    const answeredCount = getAnsweredQuestionsCount(finalResponses);
    const totalCount = getQuestionsCount(finalResponses);

    const resultJson = { result: finalResponses, answers: responseData };

    try {
      const res = await api.post(`/rest/mass-tort-client-forms`, {
        case_id: clientData?.case_id,
        client_id: clientData?.client_id,
        answered_by: 'client',
        form_otp_id: clientData.id,
        answered_count: answeredCount,
        selected_questions_data: selectedQuestionsData,
      });

      if (res?.data) {
        await uploadFile(res.data.questions_upload_url, resultJson, 'application/json');
        await api.put('/rest/mass-tort-client-forms/save-s3key-url', {
          case_id: clientData?.case_id,
          client_id: clientData?.client_id,
          questions_upload_url: res.data.questions_upload_url,
          questions_public_url: res.data.questions_public_url,
          questions_s3_file_key: res.data.questions_s3_file_key,
          questions_count: totalCount,
          answered_count: answeredCount,
          selected_questions_data: selectedQuestionsData,
        });
      }
      // Set state to show custom completion screen after successful submission/updates
      setIsSurveyCompleted(true);
    } catch (error) {
      console.error('Survey submission error:', error);
      // Show the custom screen even on API error for a cleaner transition
      setIsSurveyCompleted(true);
    }
  }, [clientData, existingQuestions, masstortQuestionsRecords, selectedQuestionsData]);

  // Attach complete event
  useEffect(() => {
    if (!survey) return;
    survey.onComplete.add(surveyComplete);
  }, [survey, surveyComplete]);

  const totalQuestions = survey ? survey.getAllQuestions().length : 1;
  const progressWidth = (editedQuestionsCount / totalQuestions) * 100;

  if (!survey) return <ClipWrapper>Loading questions...</ClipWrapper>;

  // Conditional Rendering of the Custom Completion Screen
  if (isSurveyCompleted) {
    return (
      <CenteredCompletionWrapper>
        <CustomCompletionScreen
          handleEditAnswer={handleEditAnswer}
          TargetLanguageCode={TargetLanguageCode}
          classes={classes}
        />
      </CenteredCompletionWrapper>
    );
  }

  return (
    <>
      <ProgressBarWrapper>
        <ProgressBarInner>
          <ProgressBarBase />
          <ProgressBar width={progressWidth} />
        </ProgressBarInner>
        <ProgressText>{Math.ceil(progressWidth)}%</ProgressText>
      </ProgressBarWrapper>

      {/* Use the container to add top margin */}
      <SurveyContainer>
        {/* TOP PAGINATION: Render the Custom Navigator */}
        {/* <CustomPageNavigator survey={survey} />  */}

        <Survey model={survey} />

        {/* BOTTOM PAGINATION: Render the Custom Navigator */}
        <CustomPageNavigator survey={survey} />
      </SurveyContainer>
    </>
  );
}

export default App;